import { CheckboxField, SelectField, useFormContext, useFormField } from '@calendly/form';
import { FieldLabel, Fieldset } from '@calendly/form/parts';
import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { Avatar } from '@calendly/ui/components/avatar';
import { ContextualBanner } from '@calendly/ui/components/contextual-banner';
import { AlertWarningCircleIcon, InformationCircleIcon } from '@calendly/ui/components/icons';
import { MinimalButton } from '@calendly/ui/components/minimal-button';
import { Control, Option, Options, Select } from '@calendly/ui/components/select';
import { BodyText } from '@calendly/ui/components/typography';
import classNames from 'classnames';
import { useEffect, forwardRef } from 'react';
import { useGetUserQuery } from 'common';
import { BodyField, buildDefaultNotification, CancellationPolicyField, SubjectField, Timings } from 'components/event_type_edit';
import { StatusField } from 'features/event_type_editor/control_panel/components';
import { useEventType } from 'features/event_type_provider';
import { isAnyLocksEnabled } from 'services/event_type_locking';
import { getDefaultTiming } from './config';
import { UpgradeEnforcementBanner, useShowActivationEnforcement, useShowCustomizationEnforcement } from './upgrade_enforcement';
import { useIsNotificationCustomizationRestricted } from '../../../../../../hooks/useIsNotificationCustomizationRestricted';
import { useIsSmsCustomizationRestricted } from '../../../../../../hooks/useIsSmsCustomizationRestricted';
import { FullStoryExclude, FullStoryMask } from '../../../../../../shared/fullstory_privacy';
import { useSelectedSectionsActions } from '../../../components/selected_sections';
const shouldIncludeSubject = notificationKind => ['invitee_invitation', 'invitee_cancellation', 'invitee_confirmation', 'invitee_reminder', 'invitee_email_follow_up'].includes(notificationKind);
const shouldIncludeComingFrom = (notificationKind, eventType) => {
  const {
    isParentMET,
    isCollective,
    isCollectiveRoundRobin,
    memberships,
    user_pools: userPools = []
  } = eventType;
  if (isParentMET) return false;
  const collectivePoolsUuid = userPools.find(pool => pool.pooling_type === 'collective')?.uuid;
  const collectivePoolHostsPresent = collectivePoolsUuid && memberships.some(m => collectivePoolsUuid === m.user_pool_uuid);
  return (isCollective || isCollectiveRoundRobin && collectivePoolHostsPresent) && notificationKind === 'invitee_invitation';
};
const shouldIncludeReplyTo = notificationKind => ['invitee_email_follow_up', 'invitee_reminder', 'invitee_cancellation', 'invitee_confirmation'].includes(notificationKind);
const kindSendsImmediately = notificationKind => ['invitee_invitation', 'invitee_cancellation', 'invitee_confirmation'].includes(notificationKind);
const shouldIncludeOptionalProTips = notificationKind => notificationKind === 'invitee_confirmation';
const shouldIncludeStatus = notificationKind => ['invitee_reminder', 'invitee_email_follow_up', 'invitee_sms_reminder'].includes(notificationKind);
const containerCss = "c12z3iwx";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, props.className)} />;
});
const spacingWrapperCss = "s1xapczl";
const SpacingWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(spacingWrapperCss, props.className)} />;
});
const styledFieldSetWithBackgroundCss = "s1luyug2";
const styledFieldSetCss = "s1wqe2n3";
const StyledFieldSet = forwardRef((props, ref) => {
  const {
    withBackground
  } = props;
  return <Fieldset ref={ref} {...props} className={classNames(styledFieldSetCss, {
    [styledFieldSetWithBackgroundCss]: withBackground
  }, props.className)} />;
});
const bannerWrapperCss = "ba3m3ul";
const BannerWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(bannerWrapperCss, props.className)} />;
});
const styledAvatarCss = "sow8j0i";
const StyledAvatar = forwardRef((props, ref) => <FullStoryExclude>
    <Avatar ref={ref} {...props} className={classNames(styledAvatarCss, props.className)} />
  </FullStoryExclude>);
const optionCredentialsCss = "o1bwlq9p";
const OptionCredentials = forwardRef((props, ref) => <div ref={ref} {...props} className={classNames(optionCredentialsCss, props.className)} />);
const optionWrapperCss = "oa8gzd8";
const OptionWrapper = forwardRef((props, ref) => <div ref={ref} {...props} className={classNames(optionWrapperCss, props.className)} />);
const ChangeConfirmationTypeButton = ({
  kind
}) => {
  const {
    swapCurrentSection
  } = useSelectedSectionsActions();
  if (!['invitee_confirmation', 'invitee_invitation'].includes(kind)) return null;
  const key = kind === 'invitee_invitation' ? 'email_confirmation' : 'calendar_invitation';
  const buttonText = kind === 'invitee_invitation' ? I18n.t('publisher.event_type_editor.sections.communications.invitation_confirmation.switch_to_email') : I18n.t('publisher.event_type_editor.sections.communications.invitation_confirmation.switch_to_calendar');
  const changeConfirmationType = () => swapCurrentSection({
    key,
    title: I18n.t(`publisher.event_type_editor.sections.communications.${key}`)
  });
  return <MinimalButton fontSize="medium" onClick={changeConfirmationType}>
      {buttonText}
    </MinimalButton>;
};
const NoCalendarsConnectedBanner = () => <ContextualBanner variant="danger" icon={AlertWarningCircleIcon}>
    {I18n.t('publisher.event_type_editor.sections.communications.no_calendars_connected_warning')}
  </ContextualBanner>;
const pendingMemberLabelCss = "p1hyop75";
const PendingMemberLabel = forwardRef((props, ref) => <p ref={ref} {...props} className={classNames(pendingMemberLabelCss, props.className)} />);
const getViableCalendarNotifiers = eventType => {
  const {
    isCollectiveRoundRobin,
    user_pools: userPools = [],
    memberships
  } = eventType;
  const collectivePoolUuid = userPools.find(pool => pool.pooling_type === 'collective')?.uuid;
  return isCollectiveRoundRobin ? memberships.filter(m => collectivePoolUuid === m.user_pool_uuid) : memberships;
};
const ComingFromField = () => {
  const eventType = useEventType();
  const memberships = getViableCalendarNotifiers(eventType);
  const [{
    value: assignedSenderId
  }, _meta, {
    setValue
  }] = useFormField({
    name: 'assignedSenderId'
  });
  const selected = memberships.find(m => m.id === assignedSenderId);
  const noCalendarsConnected = !memberships.some(m => m.has_active_notifiable_calendar);
  useEffect(() => {
    if (!assignedSenderId && !noCalendarsConnected) {
      setValue(memberships.find(m => m.has_active_notifiable_calendar).id);
    }
  }, [assignedSenderId, memberships, noCalendarsConnected, setValue]);
  const onChange = memberId => {
    setValue(memberId);
  };
  if (noCalendarsConnected) {
    return <NoCalendarsConnectedBanner />;
  }
  return <div data-testid="invitations_coming_from_select">
      <FieldLabel id="invitations_coming_from_label" text={I18n.t('publisher.event_type_editor.sections.communications.invitations_coming_from')} />
      <Select value={assignedSenderId} onChange={onChange}>
        <Control aria-labelledby="invitations_coming_from_label">
          {selected && <div>
            <StyledAvatar size="tiny" src={selected.avatar_url} letter={selected.avatar_letter} alt={selected.name} />
            <FullStoryMask>{selected.name}</FullStoryMask>
          </div>}
        </Control>
        <Options data-testid="invitations_coming_from_select_options">
          {memberships.map(m => <Option key={m.id} value={m.id} aria-label={m.name} disabled={!m.has_active_notifiable_calendar}>
              <OptionWrapper>
                <StyledAvatar size="small" src={m.avatar_url} letter={m.avatar_letter} alt={m.name} />
                <OptionCredentials>
                  <FullStoryMask>
                    {m.is_pending ? <PendingMemberLabel>{m.name}</PendingMemberLabel> : <div>{m.name}</div>}
                    {m.prevented_because && <div>{m.prevented_because}</div>}
                  </FullStoryMask>
                </OptionCredentials>
              </OptionWrapper>
            </Option>)}
        </Options>
      </Select>
    </div>;
};
const isSmsKind = kind => kind === 'invitee_sms_reminder';
const SmsCustomizationRestrictedBanner = () => <ContextualBanner variant="warning" icon={AlertWarningCircleIcon}>
    {I18n.t('publisher.workflows.actions.sms.customization_restricted')}
  </ContextualBanner>;
export const NotificationFields = ({
  eventType,
  kind,
  description
}) => {
  const {
    isGroup,
    isMETParent
  } = eventType;
  const cancellationDisabled = useShowCustomizationEnforcement({
    isMETParent,
    kind: 'invitee_cancellation'
  });
  const showActivationEnforcement = useShowActivationEnforcement({
    isMETParent,
    kind
  });
  const showCustomizationEnforcement = useShowCustomizationEnforcement({
    isMETParent,
    kind
  });
  const {
    data: currentUser
  } = useGetUserQuery();
  const isNotificationCustomizationRestricted = useIsNotificationCustomizationRestricted();
  const isSmsCustomizationRestricted = useIsSmsCustomizationRestricted(currentUser);
  const smsCustomizationDisabled = isSmsKind(kind) && isSmsCustomizationRestricted && !isNotificationCustomizationRestricted;
  const ehawkDisabled = isNotificationCustomizationRestricted;
  const defaultNotification = buildDefaultNotification(eventType, kind, {
    noReplyEnabled: eventType.no_reply_enabled
  });
  const includeSubject = shouldIncludeSubject(kind);
  const sendsImmediately = kindSendsImmediately(kind);
  const sendsImmediatelyText = kind === 'invitee_cancellation' ? I18n.t('publisher.event_type_editor.sections.communications.invitation_cancellation.sends_immediately') : I18n.t('publisher.event_type_editor.sections.communications.invitation_confirmation.sends_immediately');
  const includeOptionalProTips = shouldIncludeOptionalProTips(kind);
  const includeStatus = shouldIncludeStatus(kind);
  const includeReplyTo = shouldIncludeReplyTo(kind);
  const includeComingFrom = shouldIncludeComingFrom(kind, eventType);
  const showCancellationPolicy = ['invitee_reminder', 'invitee_confirmation', 'invitee_invitation'].includes(kind);
  const etLocked = !isMETParent && isAnyLocksEnabled(eventType, ['v3_notifications']);
  const {
    values: {
      noReply,
      cancellationPolicy
    } = {}
  } = useFormContext();
  return <Container>
      <UpgradeEnforcementBanner isMETParent={isMETParent} kind={kind} />
      <SpacingWrapper>
        <BodyText>
          {description}
        </BodyText>
        {!isGroup && !etLocked && <ChangeConfirmationTypeButton kind={kind} />}
      </SpacingWrapper>
      {includeComingFrom && !etLocked && <ComingFromField />}
      {includeReplyTo && <StyledFieldSet>
          <SelectField name="noReply" disabled={etLocked} label={I18n.t('publisher.event_type_editor.sections.communications.reply_to')} proTip={HtmlParser(I18n.t('publisher.event_type_editor.sections.communications.email_reminders.from_field.pro_tip'))} options={[{
        value: 'hosts',
        name: I18n.t('publisher.event_type_editor.sections.communications.email_reminders.from_field.host_email')
      }, {
        value: 'no-reply',
        name: I18n.t('publisher.event_type_editor.sections.communications.email_reminders.from_field.no_reply')
      }]} />
          <BannerWrapper>
            <ContextualBanner icon={InformationCircleIcon}>
              {I18n.t('publisher.event_type_editor.sections.communications.email_reminders.from_field.banner')}
            </ContextualBanner>
          </BannerWrapper>
        </StyledFieldSet>}
      {includeComingFrom && etLocked && <div>
          <FieldLabel text={I18n.t('publisher.event_type_editor.sections.communications.reply_to')} proTip={HtmlParser(I18n.t('publisher.event_type_editor.sections.communications.email_reminders.from_field.pro_tip'))} />
          <BodyText>
            {noReply && I18n.t(`publisher.event_type_editor.sections.communications.email_reminders.from_field.
                ${noReply === 'hosts' ? 'host_email' : 'no_reply'}`.replace(/\s/g, ''))}
          </BodyText>
        </div>}
      {includeSubject && <SubjectField eventType={eventType} name={kind} disabled={showActivationEnforcement || showCustomizationEnforcement || ehawkDisabled} includeProTip={includeOptionalProTips} etLocked={etLocked} />}
      <div>
        <BodyField eventType={eventType} name={kind} disabled={showActivationEnforcement || showCustomizationEnforcement || smsCustomizationDisabled || ehawkDisabled} includeProTip={includeOptionalProTips} etLocked={etLocked} />
        {smsCustomizationDisabled && !showActivationEnforcement && !showCustomizationEnforcement && <SmsCustomizationRestrictedBanner />}
      </div>
      {sendsImmediately && <div>
          <FieldLabel text={I18n.t('publisher.event_types.notifications.timing')} />
          <BodyText>{sendsImmediatelyText}</BodyText>
        </div>}
      {!sendsImmediately && <Timings name={kind} eventType={eventType} disabled={showActivationEnforcement || showCustomizationEnforcement} defaultValue={getDefaultTiming(defaultNotification)} useNewLabels etLocked={etLocked} />}
      {includeStatus && <StatusField name={`${kind}.enabled`} disabled={showActivationEnforcement} etLocked={etLocked} />}
      <UpgradeEnforcementBanner isMETParent={isMETParent} kind="invitee_cancellation" />
      {showCancellationPolicy && !etLocked && <StyledFieldSet withBackground>
          <CancellationPolicyField showEnforcement={cancellationDisabled} />
          <BannerWrapper>
            <ContextualBanner icon={InformationCircleIcon}>
              {I18n.t('publisher.event_type_editor.sections.communications.cancellation_info_box')}
            </ContextualBanner>
          </BannerWrapper>
          <CheckboxField name="cancellationAllowed" disabled={cancellationDisabled} label={I18n.t('publisher.event_type_editor.sections.communications.invitation_confirmation.include_links')} spacingTop="8" spacingBottom="8" />
        </StyledFieldSet>}
      {showCancellationPolicy && etLocked && <div>
          <FieldLabel text={I18n.t('publisher.event_types.notifications.cancellation_policy.policy_label')} proTip={I18n.t('publisher.event_types.notifications.cancellation_policy.policy_label_tip')} />
          <BodyText>
            {cancellationPolicy}
          </BodyText>
          {cancellationPolicy && <br />}
          <BodyText>
            {I18n.t('publisher.managed_event_types.event_type.locked.sections.communications.invitation_confirmation.include_links')}
          </BodyText>
        </div>}
    </Container>;
};

require("./notification_fields.wyw-in-js.css!=!../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./notification_fields.js");